// Shared building blocks: layout, buttons, cards, form, page meta.
import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { GrainDefs, Art } from './illustrations';
import { DEFAULTS } from './liveContent';
import { brand, contact, nav, footer, services, interests, contactPage } from './content';
// ---------------------------------------------------------------- helpers
export function usePageMeta(meta) {
  useEffect(() => {
    if (!meta) return;
    document.title = meta.title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'description');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', meta.description || '');
  }, [meta]);
}
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView(); return; }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}
// Backend URL: CRA replaces process.env.REACT_APP_* at build time.
let API_BASE = '';
try { API_BASE = process.env.REACT_APP_BACKEND_URL || ''; } catch (e) { API_BASE = ''; }
// EMERGENT: point this at the existing contact endpoint if it differs.
export async function submitContact(data) {
  const res = await fetch(`${API_BASE}/api/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Contact form failed: ${res.status}`);
  return res.json().catch(() => ({}));
}
// ---------------------------------------------------------------- small parts
export function Button({ to, href, variant = 'primary', children, ...rest }) {
  const cls = `sg-btn sg-btn--${variant}`;
  if (to) return <Link className={cls} to={to} {...rest}>{children}</Link>;
  return <a className={cls} href={href} {...rest}>{children}</a>;
}
export function Section({ tone, tight, id, children }) {
  const cls = ['sg-section', tone && `sg-section--${tone}`, tight && 'sg-section--tight'].filter(Boolean).join(' ');
  return (
    <section className={cls} id={id}>
      <div className="sg-container">{children}</div>
    </section>
  );
}
export function Head({ eyebrow, title, lead, center, as = 'h2' }) {
  const H = as;
  return (
    <div className={`sg-head${center ? ' sg-head--center' : ''}`}>
      {eyebrow && <div className="sg-eyebrow">{eyebrow}</div>}
      <H className={as === 'h1' ? 'sg-h1' : 'sg-h2'}>{title}</H>
      {lead && <p className="sg-lead" style={center ? { marginLeft: 'auto', marginRight: 'auto' } : undefined}>{lead}</p>}
    </div>
  );
}
export function PageHero({ eyebrow, title, lead, art, children }) {
  return (
    <Section>
      <div className="sg-split">
        <div>
          {eyebrow && <div className="sg-eyebrow">{eyebrow}</div>}
          <h1 className="sg-h1">{title}</h1>
          {lead && <p className="sg-lead">{lead}</p>}
          {children}
        </div>
        {art && <Art name={art} />}
      </div>
    </Section>
  );
}
export function Ticks({ items }) {
  return <ul className="sg-ticks">{items.map((t) => <li key={t}>{t}</li>)}</ul>;
}
export function Steps({ items }) {
  if (!items || !items.length) return null;
  return (
    <ol className="sg-steps">
      {items.map((s) => (
        <li key={s.title}>
          <h3>{s.title}</h3>
          <p className="sg-muted">{s.text}</p>
        </li>
      ))}
    </ol>
  );
}
export function ServiceCard({ s }) {
  return (
    <Link className="sg-card" to={`/what-we-do/${s.slug}`}>
      <Art name={s.art} />
      <div className="sg-card__body">
        <div className="sg-card__title">{s.title}</div>
        <p className="sg-muted">{s.short}</p>
        <span className="sg-card__more">Learn more →</span>
      </div>
    </Link>
  );
}
const initials = (name) => name.replace(/^Dr\.?\s*/i, '').split(/\s+/).map((w) => w[0]).slice(0, 2).join('');
export function Person({ name, role, photo, showContact }) {
  return (
    <div className="sg-person">
      <div className="sg-avatar">
        {photo ? <img src={photo} alt={name} /> : <span aria-hidden="true">{name ? initials(name) : 'S'}</span>}
      </div>
      <div>
        <b>{name || role}</b>
        {name && <div className="sg-muted">{role}</div>}
        {showContact && (
          <div className="sg-small" style={{ marginTop: 4 }}>
            <a className="sg-link" href={contact.phoneHref}>{contact.phone}</a>
            {' · '}
            <a className="sg-link" href={contact.whatsappHref} target="_blank" rel="noreferrer">WhatsApp</a>
          </div>
        )}
      </div>
    </div>
  );
}
export function Facts({ items }) {
  return (
    <div className={`sg-facts${items.length === 4 ? ' sg-facts--4' : ''}`}>
      {items.map((f) => (
        <div className="sg-fact" key={f.label}><b>{f.value}</b><span>{f.label}</span></div>
      ))}
    </div>
  );
}
export function PartnerBand({ title, text }) {
  return (
    <Section tone="leaf">
      <div className="sg-split">
        <div>
          <h2 className="sg-h2">{title}</h2>
          <p className="sg-lead">{text}</p>
          <div className="sg-actions"><Button to="/partner-with-us" variant="light">Partner with us</Button></div>
        </div>
        <div className="sg-route" aria-label="Our first kitchen is in Pokhara. Your district could be next.">
          <div className="sg-route__stop sg-route__stop--here"><b>Kitchen 01</b><span className="sg-muted">Pokhara</span></div>
          <div className="sg-route__line" aria-hidden="true" />
          <div className="sg-route__stop sg-route__stop--next"><b>Your district</b><span>next?</span></div>
        </div>
      </div>
    </Section>
  );
}
// ---------------------------------------------------------------- contact form
export function ContactForm({ defaultInterest = '' }) {
  const [state, setState] = useState('idle'); // idle | sending | sent | failed
  const onSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const data = Object.fromEntries(form.entries());
    setState('sending');
    try {
      await submitContact(data);
      setState('sent');
      e.target.reset();
    } catch (err) {
      setState('failed');
    }
  };
  if (state === 'sent') return <p className="sg-note sg-note--ok" role="status">{contactPage.sent}</p>;
  return (
    <form className="sg-form" onSubmit={onSubmit}>
      <div className="sg-form__row">
        <div className="sg-field"><label htmlFor="sg-name">Your name *</label><input id="sg-name" name="name" required autoComplete="name" /></div>
        <div className="sg-field"><label htmlFor="sg-org">Organisation</label><input id="sg-org" name="organization" placeholder="School, hospital or company" /></div>
      </div>
      <div className="sg-form__row">
        <div className="sg-field"><label htmlFor="sg-email">Email *</label><input id="sg-email" name="email" type="email" required autoComplete="email" /></div>
        <div className="sg-field"><label htmlFor="sg-phone">Phone or WhatsApp</label><input id="sg-phone" name="phone" type="tel" autoComplete="tel" /></div>
      </div>
      <div className="sg-form__row">
        <div className="sg-field"><label htmlFor="sg-district">District or city</label><input id="sg-district" name="district" placeholder="e.g. Kaski" /></div>
        <div className="sg-field">
          <label htmlFor="sg-interest">I am interested in</label>
          <select id="sg-interest" name="interest" defaultValue={defaultInterest}>
            <option value="">Choose one</option>
            {((interests || []).filter(Boolean).length ? interests.filter(Boolean) : DEFAULTS.interests).map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
      </div>
      <div className="sg-field"><label htmlFor="sg-message">Message *</label><textarea id="sg-message" name="message" required /></div>
      {state === 'failed' && <p className="sg-note sg-note--err" role="alert">{contactPage.failed}</p>}
      <div><button className="sg-btn sg-btn--primary" type="submit" disabled={state === 'sending'}>{state === 'sending' ? 'Sending…' : 'Send message'}</button></div>
    </form>
  );
}
// ---------------------------------------------------------------- header / footer
function Logo() {
  return (
    <Link to="/" className="sg-logo" aria-label={`${brand.name}, home`}>
      {brand.logo ? <img src={brand.logo} alt="" /> : <span className="sg-logo__mark" aria-hidden="true">सर्वगुण</span>}
      <span className="sg-logo__text">{brand.name}</span>
    </Link>
  );
}
function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  return (
    <header className="sg-header">
      <div className="sg-container sg-header__inner">
        <Logo />
        <nav className="sg-nav" aria-label="Main">
          {nav.map((n) => <NavLink key={n.to} to={n.to}>{n.label}</NavLink>)}
        </nav>
        <div className="sg-header__actions">
          <Button to="/contact">Contact</Button>
          <button className="sg-menu-btn" type="button" aria-expanded={open} aria-controls="sg-mobile" aria-label={open ? 'Close menu' : 'Open menu'} onClick={() => setOpen(!open)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>
      {open && (
        <nav className="sg-mobile" id="sg-mobile" aria-label="Main">
          {nav.map((n) => <NavLink key={n.to} to={n.to}>{n.label}</NavLink>)}
          <NavLink to="/contact">Contact</NavLink>
        </nav>
      )}
    </header>
  );
}
function Footer() {
  const social = Object.entries(contact.social).filter(([, url]) => url);
  const label = { facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn', x: 'X' };
  return (
    <footer className="sg-footer">
      <div className="sg-container">
        <div className="sg-footer__grid">
          <div className="sg-footer__brand">
            <Logo />
            <p>{footer.blurb}</p>
          </div>
          <div>
            <h4>What we do</h4>
            <ul>{services.map((s) => <li key={s.slug}><Link to={`/what-we-do/${s.slug}`}>{s.title}</Link></li>)}</ul>
          </div>
          <div>
            <h4>Sarvaguna</h4>
            <ul>
              <li><Link to="/how-we-work">How we work</Link></li>
              <li><Link to="/partner-with-us">Partner with us</Link></li>
              <li><Link to="/academy">Academy & Jobs</Link></li>
              <li><Link to="/about">About us</Link></li>
              <li><Link to="/stories">Stories</Link></li>
            </ul>
          </div>
          <div>
            <h4>Contact</h4>
            <ul>
              <li><a href={contact.phoneHref}>{contact.phone}</a></li>
              <li><a href={`mailto:${contact.email}`}>{contact.email}</a></li>
              <li>{contact.kitchenLabel}: {contact.kitchen}</li>
              <li>{footer.partnersLine}</li>
              {social.map(([k, url]) => <li key={k}><a href={url} target="_blank" rel="noreferrer">{label[k]}</a></li>)}
            </ul>
          </div>
        </div>
        <div className="sg-footer__bottom">
          <span>© {new Date().getFullYear()} {brand.name}</span>
          <span>Pokhara, Nepal</span>
        </div>
      </div>
    </footer>
  );
}
export function Layout({ children }) {
  return (
    <div className="sg">
      <GrainDefs />
      <ScrollToTop />
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
